import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Menu, Phone, MapPin, Mail, Star, ChevronRight, MessageCircle, Instagram, Facebook, Calendar, Users, Accessibility, Armchair, CheckCircle, Car, ArrowLeft, Clock, Award, Heart, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import ChatWidget from '@/components/ChatWidget';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/cd5s20vv_isalon2022-01.png";

const AboutPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [startBookingOnOpen, setStartBookingOnOpen] = useState(false);

  const openChatForBooking = () => {
    setStartBookingOnOpen(true);
    setChatOpen(true);
  };

  const handleChatOpenChange = (open) => {
    setChatOpen(open);
    if (!open) {
      setStartBookingOnOpen(false);
    }
  };

  const teamMembers = [
    { name: "Emma B", role: "Director", specialty: "" },
    { name: "Charlotte", role: "Senior Stylist", specialty: "" },
    { name: "Lorraine", role: "Senior Stylist", specialty: "" },
    { name: "Emma S", role: "Stylist", specialty: "" }
  ];

  const milestones = [];

  const features = [
    { icon: Clock, title: "Established Since 2012", description: "Over a decade of trusted service to Southport and beyond" },
    { icon: Users, title: "Busy & Friendly Team", description: "A welcoming atmosphere where clients feel like family" },
    { icon: Armchair, title: "12 Styling Sections", description: "Plenty of space for comfortable, relaxed appointments" },
    { icon: Award, title: "4.9 Star Rating", description: "95+ five-star Google reviews from happy clients" },
    { icon: Accessibility, title: "Wheelchair Accessible", description: "Full accessibility with no steps for all our valued clients" },
    { icon: Car, title: "Disabled Parking", description: "Convenient disabled parking available right outside" },
    { icon: MapPin, title: "Prime Location", description: "Town centre location, trusted by local hotels" },
    { icon: Wifi, title: "Free WiFi", description: "Complimentary WiFi for all our clients" }
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="about-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>About Us | iSalon UK | Southport Hair Salon Since 2012</title>
        <meta name="description" content="Learn about iSalon UK, Southport's premier hair salon since 2012. Meet our experienced team, discover our accessible facilities, and see why we have 95+ five-star reviews." />
        <meta name="keywords" content="about isalon, hair salon southport, experienced stylists, wheelchair accessible salon, southport hairdresser" />
        <link rel="canonical" href="https://www.isalon.uk/about" />
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center" data-testid="logo">
              <img src={LOGO_URL} alt="iSalon UK" className="h-10 md:h-12 w-auto" />
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">
                Home
              </Link>
              <span className="nav-link font-sans text-sm font-medium text-isalonuk-accent">
                About
              </span>
              <Link to="/prices" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">
                Price List
              </Link>
              <Link to="/opportunities" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">
                Join Us
              </Link>
              <Button onClick={openChatForBooking} className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full px-6">
                Book Now
              </Button>
            </div>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <button className="p-2 rounded-lg text-white">
                  <Menu size={40} strokeWidth={2.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm bg-black border-l border-white/10 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-white/10">
                    <img src={LOGO_URL} alt="iSalon UK" className="h-10 w-auto mb-4" />
                    <SheetClose asChild>
                      <Button onClick={openChatForBooking} className="w-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full py-5 text-lg">
                        <Calendar className="mr-2" size={20} />
                        Book Now
                      </Button>
                    </SheetClose>
                  </div>
                  <div className="flex-1 p-6 overflow-y-auto">
                    <nav className="flex flex-col gap-4">
                      <SheetClose asChild>
                        <Link to="/" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Home
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <div className="py-3 px-4 rounded-xl font-sans text-lg font-medium text-isalonuk-accent bg-isalonuk-accent/10">
                        About
                      </div>
                      <SheetClose asChild>
                        <Link to="/prices" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Price List
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/opportunities" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Join Us
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/#contact" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Contact Us
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                    </nav>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 px-6 md:px-12 relative" data-testid="about-hero">
        {/* Mobile background image */}
        <div className="absolute inset-0 md:hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/8roxryao_about.jpg"
            alt="iSalon Interior"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-isalonuk-accent transition-colors mb-8">
            <ArrowLeft size={18} />
            <span className="font-sans text-sm">Back to Home</span>
          </Link>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-isalonuk-accent font-sans text-sm md:text-base font-medium tracking-widest uppercase mb-4">
                About iSalon UK
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
                Southport's Premier Hair Salon
              </h1>
              <p className="text-white/70 font-sans text-base md:text-lg leading-relaxed mb-6">
                iSalon is a modern hairdressers located in Southport town centre. Since 2012, we have built up a solid reputation for being one of the best hair salons in Southport, trusted by both locals and visitors alike.
              </p>
              <p className="text-white/70 font-sans text-base md:text-lg leading-relaxed mb-8">
                Our friendly, busy salon offers a welcoming atmosphere where everyone feels at home. With 12 styling sections and a dedicated team of experienced stylists, we have plenty of room to accommodate all your hair care needs.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button onClick={openChatForBooking} size="lg" className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-semibold rounded-full px-8">
                  <Calendar className="mr-2" size={18} />
                  Book Appointment
                </Button>
              </div>
            </div>
            
            <div className="relative hidden md:block">
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/8roxryao_about.jpg"
                  alt="iSalon Interior"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
              {/* Google Reviews Badge */}
              <a 
                href="https://www.google.com/search?q=isalon+southport&rlz=1C1CHBF_en-GBGB926GB926#"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute -bottom-6 -left-6 bg-black border border-white/10 rounded-2xl p-5 shadow-xl hover:border-isalonuk-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                    <img src="https://www.google.com/favicon.ico" alt="Google" className="w-7 h-7" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={14} fill="#97A301" className="text-isalonuk-accent" />
                      ))}
                      <span className="text-white font-bold ml-1">4.9</span>
                    </div>
                    <p className="text-white/60 font-sans text-xs">95+ Google Reviews</p>
                  </div>
                </div>
              </a>
              {/* Facebook Reviews Badge */}
              <a 
                href="https://www.facebook.com/isalonuk/reviews/?id=100056815094879&sk=reviews"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute -bottom-6 right-6 bg-black border border-white/10 rounded-2xl p-5 shadow-xl hover:border-isalonuk-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#1877F2] rounded-xl flex items-center justify-center">
                    <Facebook size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={14} fill="#97A301" className="text-isalonuk-accent" />
                      ))}
                      <span className="text-white font-bold ml-1">5.0</span>
                    </div>
                    <p className="text-white/60 font-sans text-xs">50+ Facebook Reviews</p>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-16 bg-zinc-900 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="font-heading text-4xl md:text-5xl font-bold text-isalonuk-accent">12+</p>
              <p className="text-white/60 font-sans text-sm mt-2">Years Experience</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-4xl md:text-5xl font-bold text-isalonuk-accent">12</p>
              <p className="text-white/60 font-sans text-sm mt-2">Styling Sections</p>
            </div>
            <a 
              href="https://search.google.com/local/reviews?placeid=ChIJlcnpXMM-e0gR-CIRDdkmCXo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-center hover:opacity-80 transition-opacity"
            >
              <p className="font-heading text-4xl md:text-5xl font-bold text-isalonuk-accent">95+</p>
              <p className="text-white/60 font-sans text-sm mt-2">5-Star Reviews</p>
            </a>
            <a 
              href="https://search.google.com/local/reviews?placeid=ChIJlcnpXMM-e0gR-CIRDdkmCXo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-center hover:opacity-80 transition-opacity"
            >
              <p className="font-heading text-4xl md:text-5xl font-bold text-isalonuk-accent">4.9</p>
              <p className="text-white/60 font-sans text-sm mt-2">Google Rating</p>
            </a>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-isalonuk-accent font-sans text-sm font-medium tracking-widest uppercase mb-3">What Makes Us Special</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-bold">
              Welcome to iSalon
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border border-white/10 bg-zinc-900 rounded-2xl p-6" data-testid={`feature-card-${index}`}>
                <div className="w-12 h-12 bg-isalonuk-accent/20 rounded-full flex items-center justify-center mb-4">
                  <feature.icon className="text-isalonuk-accent" size={24} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-white/60 font-sans text-sm leading-relaxed">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-zinc-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-isalonuk-accent font-sans text-sm font-medium tracking-widest uppercase mb-3">Our Team</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-bold mb-4">
              Meet the Stylists
            </h2>
            <p className="text-white/60 font-sans max-w-2xl mx-auto">
              Our experienced team of stylists have many years in the hairdressing industry and have completed courses and training qualifications in many areas.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamMembers.map((member, index) => (
              <Card key={index} className="border border-white/10 bg-black rounded-2xl p-6 text-center" data-testid={`team-card-${index}`}>
                <div className="w-20 h-20 bg-isalonuk-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-heading text-2xl font-bold text-isalonuk-accent">{member.name[0]}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-1">{member.name}</h3>
                <p className="text-isalonuk-accent font-sans text-sm font-medium mb-2">{member.role}</p>
                {member.specialty && (
                  <p className="text-white/60 font-sans text-sm">{member.specialty}</p>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Accessibility Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/rdmn0hwe_rentachair.jpg" 
            alt="iSalon Interior" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-isalonuk-accent font-sans text-sm font-medium tracking-widest uppercase mb-3">Accessibility</p>
              <h2 className="font-heading text-3xl md:text-4xl text-white font-bold mb-6">
                Everyone is Welcome
              </h2>
              <p className="text-white/70 font-sans text-base md:text-lg leading-relaxed mb-6">
                At iSalon, we believe everyone deserves to look and feel their best. Our salon is fully wheelchair accessible, with plenty of room for comfortable movement throughout our 12 styling sections.
              </p>
              <p className="text-white/70 font-sans text-base md:text-lg leading-relaxed mb-8">
                We also offer disabled parking directly outside our premises, making it easy for all our clients to visit us.
              </p>
              
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-isalonuk-accent/20 rounded-full flex items-center justify-center">
                    <Accessibility className="text-isalonuk-accent" size={20} />
                  </div>
                  <span className="text-white font-sans">Wheelchair Friendly</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-isalonuk-accent/20 rounded-full flex items-center justify-center">
                    <Car className="text-isalonuk-accent" size={20} />
                  </div>
                  <span className="text-white font-sans">Disabled Parking</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-isalonuk-accent/20 rounded-full flex items-center justify-center">
                    <Wifi className="text-isalonuk-accent" size={20} />
                  </div>
                  <span className="text-white font-sans">Free Wi-Fi</span>
                </div>
              </div>
            </div>
            
            <div className="hidden md:block">
              <Card className="border border-white/20 bg-black/60 backdrop-blur-sm rounded-2xl p-8">
                <h3 className="font-heading text-2xl font-bold text-white mb-6">Visit Us</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="text-isalonuk-accent flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-white font-sans font-medium">Address</p>
                      <p className="text-white/60 font-sans text-sm">89B London St, Southport, PR9 0TX</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="text-isalonuk-accent flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-white font-sans font-medium">Phone</p>
                      <a href="tel:01704500872" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">01704 500872</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="text-isalonuk-accent flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-white font-sans font-medium">Email</p>
                      <a href="mailto:bookings@isalon.uk" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">bookings@isalon.uk</a>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 pt-6 border-t border-white/10">
                  <a href="https://maps.google.com/?q=89B+London+Street+Southport+PR9+0TX" target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full">
                      <MapPin className="mr-2" size={18} />
                      Get Directions
                    </Button>
                  </a>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-isalonuk-accent/20 to-isalonuk-accent/10 border border-isalonuk-accent/30 rounded-3xl p-8 md:p-12 text-center">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Book an Appointment?
            </h3>
            <p className="text-white/70 font-sans mb-8 max-w-lg mx-auto">
              Request your appointment today and experience Southport's premier hair salon.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={openChatForBooking} size="lg" className="w-full sm:w-auto bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-semibold rounded-full px-8">
                <Calendar className="mr-2" size={18} />
                Book Online Now
              </Button>
              <Link to="/opportunities">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/40 text-white hover:bg-white/10 rounded-full px-8">
                  <Users className="mr-2" size={18} />
                  Join Our Team
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="mb-4">
                <img src={LOGO_URL} alt="iSalon UK" className="h-10 w-auto" />
              </div>
              <p className="text-white/60 font-sans text-sm leading-relaxed mb-1">
                Established in 2012
              </p>
              <p className="text-white/60 font-sans text-sm leading-relaxed mb-4">
                Experience · Knowledge · Skill · Quality
              </p>
              <div className="flex gap-3">
                <a href="https://www.facebook.com/226899317381783" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-isalonuk-accent transition-colors">
                  <Facebook size={18} className="text-white" />
                </a>
                <a href="https://www.instagram.com/isalonuk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-isalonuk-accent transition-colors">
                  <Instagram size={18} className="text-white" />
                </a>
                <a href="https://twitter.com/isalonuk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-isalonuk-accent transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-sans font-semibold text-white mb-4">Quick Links</h4>
              <nav className="flex flex-col gap-2">
                <Link to="/about" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">About Us</Link>
                <Link to="/#services" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">Services</Link>
                <Link to="/opportunities" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">Join Us</Link>
                <Link to="/#contact" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">Contact</Link>
              </nav>
            </div>

            <div>
              <h4 className="font-sans font-semibold text-white mb-4">Contact</h4>
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-2">
                  <MapPin size={16} className="text-isalonuk-accent mt-1 flex-shrink-0" />
                  <span className="text-white/60 font-sans text-sm">89B London St, Southport, PR9 0TX</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <a href="tel:01704500872" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">01704 500872</a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <a href="mailto:bookings@isalon.uk" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">bookings@isalon.uk</a>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <button onClick={openChatForBooking} className="text-white/60 font-sans text-sm hover:text-isalonuk-accent text-left">Request Appointment</button>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="text-white/40 font-sans text-sm">© {new Date().getFullYear()} iSalon UK. All rights reserved.</p>
              <p className="text-white/40 font-sans text-xs leading-relaxed max-w-lg">
                iSalon UK is a trading name of Scissors Limited, a company registered in England and Wales with company registration number 11875206.
              </p>
              <div className="flex flex-col items-center gap-1">
                <p className="text-isalonuk-accent font-sans text-sm font-medium">
                  Website designed and built by <a href="https://www.barton.uk" target="_blank" rel="noopener noreferrer" className="hover:underline">www.barton.uk</a>
                </p>
                <Link to="/privacy-policy" className="text-white/40 font-sans text-xs hover:text-isalonuk-accent transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* AI Chat Widget */}
      <ChatWidget 
        isOpen={chatOpen} 
        onOpenChange={handleChatOpenChange} 
        startBookingOnOpen={startBookingOnOpen}
      />
    </div>
  );
};

export default AboutPage;
